import React, { useEffect, useState, useCallback } from 'react';
import { AlertTriangle, RefreshCw, X } from 'lucide-react';
import { getCounts, retryFailed } from '../lib/syncQueue';

/**
 * OfflineFailureBanner — quiet by default, surfaces only when the sync queue
 * has items that exhausted their retry budget (3 attempts). One-tap retry.
 *
 * Per the silent-service principle: 99% of users will never see this.
 * It only renders when there's a real, persistent problem the user might
 * otherwise lose data from.
 *
 * Position: directly under the navbar, full-width amber-toned strip.
 * Dismissible per-session (re-appears on next mount if failures persist).
 */
const OfflineFailureBanner = () => {
  const [counts, setCounts] = useState({ pending: 0, failed: 0 });
  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy] = useState(false);

  const poll = useCallback(async () => {
    try {
      setCounts(await getCounts());
    } catch { /* idb not ready yet */ }
  }, []);

  useEffect(() => {
    poll();
    const id = setInterval(poll, 10000); // 10s — cheap, IDB read is fast
    const onlineHandler = () => poll();
    window.addEventListener('online', onlineHandler);
    return () => {
      clearInterval(id);
      window.removeEventListener('online', onlineHandler);
    };
  }, [poll]);

  const handleRetry = async () => {
    setBusy(true);
    try {
      await retryFailed();
      // Give the drain a beat to finish then refresh counts
      setTimeout(poll, 1200);
    } finally {
      setBusy(false);
    }
  };

  if (counts.failed === 0 || dismissed) return null;

  const word = counts.failed === 1 ? 'entry' : 'entries';

  return (
    <div
      className="sticky top-16 z-40 bg-amber-500/10 border-b border-amber-500/40 px-4 py-2 flex items-center justify-between gap-3 text-sm"
      data-testid="offline-failure-banner"
    >
      <div className="flex items-center gap-2 text-amber-200 min-w-0">
        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
        <span className="truncate">
          <span className="font-semibold">{counts.failed}</span> {word} waiting to sync
        </span>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={handleRetry}
          disabled={busy}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-amber-500/20 hover:bg-amber-500/30 text-amber-100 text-xs font-semibold disabled:opacity-50 transition-colors"
          data-testid="offline-failure-retry"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${busy ? 'animate-spin' : ''}`} /> Retry
        </button>
        <button
          onClick={() => setDismissed(true)}
          className="p-1 text-amber-300/70 hover:text-enf-text-primary transition-colors"
          data-testid="offline-failure-dismiss"
          aria-label="Dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export default OfflineFailureBanner;
